import React from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, MapPin, MountainSnow } from "lucide-react";
import { fetchAdminUser, fetchAdminUserActivityReview } from "../../api/admin";
import { ReviewMap, buildRows, type RowModel } from "../ActivityReviewPage";
import { AtlasPanel } from "../../components/AtlasPanel";
import { Badge } from "../../components/Badge";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

// Read-only admin mirror of the user's own review page: same map and the same
// matched / near-miss row model, minus the add/remove controls. Corrections
// stay owner-only.
function ReadOnlyRow({ row, mode }: { row: RowModel; mode: "matched" | "nearMiss" }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          background: "var(--surface-raised)",
          color: mode === "matched" ? "var(--spruce-300)" : "var(--sky-300)",
        }}
      >
        {row.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13.5,
            fontWeight: 600,
            color: "var(--text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.name}
        </div>
        <div className="eyebrow" style={{ fontSize: 10, marginTop: 2 }}>
          {row.meta}
        </div>
      </div>
    </div>
  );
}

export default function AdminUserActivityPage() {
  const { user: me } = useAuth();
  const { userId, activityId } = useParams({ strict: false }) as {
    userId: string;
    activityId: string;
  };

  const userQuery = useQuery({
    queryKey: ["admin/users", userId],
    queryFn: () => fetchAdminUser(userId),
    enabled: !!me?.isAdmin && !!userId,
  });

  const reviewQuery = useQuery({
    queryKey: ["admin/users", userId, "activities", activityId, "review"],
    queryFn: () => fetchAdminUserActivityReview(userId, activityId),
    enabled: !!me?.isAdmin && !!userId && !!activityId,
  });

  if (!me?.isAdmin) return <AccessDenied />;

  const target = userQuery.data?.user;
  const data = reviewQuery.data;
  const rows = data ? buildRows(data) : null;

  return (
    <AdminShell title="Users">
      <div>
        <Link
          to="/admin/users/$userId/activities"
          params={{ userId }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            color: "var(--text-secondary)",
            textDecoration: "none",
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 10,
          }}
        >
          <ChevronLeft size={16} /> All activities
        </Link>
        {data && (
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              {target ? `${target.name ?? target.email} · ` : ""}
              {new Date(data.activity.startedAt).toLocaleDateString()} · {data.activity.sourceType}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>
                {data.activity.name}
              </h2>
              <Badge variant="neutral">Read-only</Badge>
            </div>
          </div>
        )}
      </div>

      {reviewQuery.isLoading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <Skel height="380px" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <Skel height="260px" />
            <Skel height="260px" />
          </div>
        </div>
      ) : !data || !rows ? (
        <EmptyState icon={<MapPin size={18} />} title="Activity not found" />
      ) : (
        <>
          <div
            style={{
              position: "relative",
              height: 380,
              borderRadius: "var(--radius-lg)",
              overflow: "hidden",
              border: "1px solid var(--border-subtle)",
              boxShadow: "var(--ring-top)",
            }}
          >
            <ReviewMap data={data} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 20 }}>
            <AtlasPanel
              title="Collected on this activity"
              action={<Badge variant="success" dot>{rows.matched.length}</Badge>}
            >
              {rows.matched.length === 0 ? (
                <EmptyState
                  icon={<MountainSnow size={18} />}
                  title="Nothing collected"
                  hint="This activity didn't match any peaks, landmarks or trail sections."
                />
              ) : (
                rows.matched.map((row) => (
                  <ReadOnlyRow key={`${row.itemType}:${row.itemId}`} row={row} mode="matched" />
                ))
              )}
            </AtlasPanel>

            <AtlasPanel
              title="Close but not collected"
              action={<Badge variant="info" dot>{rows.nearMisses.length}</Badge>}
            >
              {rows.nearMisses.length === 0 ? (
                <EmptyState
                  icon={<MapPin size={18} />}
                  title="No near misses"
                  hint="Nothing else sits within 250 m of this route."
                />
              ) : (
                rows.nearMisses.map((row) => (
                  <ReadOnlyRow key={`${row.itemType}:${row.itemId}`} row={row} mode="nearMiss" />
                ))
              )}
            </AtlasPanel>
          </div>
        </>
      )}
    </AdminShell>
  );
}
